/** Bilet talebi listesi tip tanımları (no-any). Backend DTO'larıyla birebir. */

/** REFERANS(TYPE='TICKET_REQUEST_STATUS') id'leri — backend `TicketRequest.SystemStatus` ile birebir. */
export const TICKET_REQUEST_STATUS = {
  PENDING: 300,
  APPROVED: 301,
  REJECTED: 302,
} as const

export interface LookupItem {
  id: number
  name: string
}

export interface TicketRequestListItem {
  id: number
  pnr: string
  nameSurname: string
  type: LookupItem
  office: LookupItem
  status: LookupItem
  ticketPrice: number | null
  creDate: string
  /**
   * Talep sistemde işlendi mi (onay/ret aksiyonu uygulandı mı). Reddedildi durumunda buton mantığını
   * belirler: girişte "Reddedildi" seçilmiş (isProcessed=false) → yalnız Onaylanabilir; sistemden
   * reddedilmiş (isProcessed=true) → işlem sonlanmıştır (buton yok). Beklemede/Onaylandı'da etkisiz.
   */
  isProcessed?: boolean
  /** Optimistic lock için; onay/ret isteğinde geri gönderilir (backend PUT modDate zorunlu). */
  modDate: string
}

/** Çözülmüş kullanıcı referansı (backend `UserRefDto`); ad USERS'tan, yoksa `#id`. */
export interface UserRef {
  id: number
  displayName: string
}

/**
 * Talep detayı (backend `TicketRequestDetailDto` ile birebir). Salt-okuma; liste satırından zengin.
 * Backend null alanları JSON'dan çıkarır → nullable alanlar opsiyonel (`?`) ve `| null` ile tiplenir;
 * tüketen kod `== null` ile hem `null` hem `undefined`'ı karşılar. İşlem Geçmişi ayrı alan değildir —
 * `creDate`, `status`, `approveOrRejectTime`, `approveOrRejectUser`, `requestUser`'dan türetilir.
 */
export interface TicketRequestDetail {
  id: number
  pnr: string
  nameSurname: string
  type: LookupItem
  office: LookupItem
  status: LookupItem
  ticketPrice?: number | null
  requestNote: string
  otherRequestNote?: string | null
  rejectReason?: LookupItem | null
  rejectNote?: string | null
  /** Talep eden; REQUEST_USER_ID nullable olduğundan backend hiç göndermeyebilir (opsiyonel). */
  requestUser?: UserRef | null
  creDate: string
  approveOrRejectTime?: string | null
  approveOrRejectUser?: UserRef | null
  modDate: string
}

/** Backend Spring PagedModel biçimi: içerik + iç içe `page` meta (anayasa API Standards). */
export interface PageResponse<T> {
  content: T[]
  page: {
    size: number
    number: number
    totalElements: number
    totalPages: number
  }
}

export interface TicketSummary {
  pending: number
  approved: number
  rejected: number
  approvedTotalAmount: number
}

export interface TicketLookups {
  statuses: LookupItem[]
  offices: LookupItem[]
  types: LookupItem[]
  rejectReasons: LookupItem[]
}

/** Onay isteği gövdesi (backend `TicketApproveDto`); yalnız optimistic lock için `modDate`. */
export interface TicketApprove {
  modDate: string
}

/**
 * Ret isteği gövdesi (backend `TicketRejectDto`). `rejectReasonId` zorunlu; gerekçe "Diğer" (id=-1)
 * ise `rejectNote` zorunlu (sunucu da doğrular). `modDate` optimistic lock.
 */
export interface TicketReject {
  rejectReasonId: number
  rejectNote?: string
  modDate: string
}

/** Liste sorgu parametreleri. Filtreler null/undefined ise gönderilmez (kapsam geneli). */
export interface TicketQuery {
  statusId?: number | null
  officeId?: number | null
  typeId?: number | null
  q?: string
  /**
   * Özel kalem kapsamı (037) — tam PNR eşleşmesi. Doluysa, o PNR'a uyan (başka personelin dahil) talepler
   * listelenir; backend yalnız TICKET_REQUEST_VIEW_OWN_OR_IDENTIFIER yetkisinde onurlandırır (diğerlerinde yok sayılır).
   */
  exactPnr?: string
  /** Özel kalem kapsamı (037) — tam Ad-Soyad eşleşmesi (Türkçe-farkında; bkz. exactPnr). */
  exactName?: string
  page?: number
  size?: number
}

/**
 * Yeni talep isteği gövdesi (backend `TicketRequestCreateDto` ile birebir). Durum gönderilmez —
 * yeni talep sunucuda daima "Beklemede" atanır; talep-eden de gönderilmez (sistem kullanıcısı).
 * `ticketPrice` opsiyonel.
 */
export interface TicketRequestCreate {
  pnr: string
  nameSurname: string
  typeId: number
  officeId: number
  /** Durum kullanıcı seçimli; oluşturmada yalnız Beklemede (300) veya Reddedildi (302). */
  statusId: number
  ticketPrice: number | null
  requestNote: string
  /** Talep türü "Diğer" (id=-1) ise zorunlu açıklama; genel nottan ayrı. Diğer değilse null (008). */
  otherRequestNote?: string | null
}
